import { useEffect, useState } from 'react';
import { renovacaoPedida } from '@/lib/copiaLocal';
import { ultimoUid } from '@/lib/ultimoUid';
import { recarregarPagina } from '@/lib/versao';

/**
 * ★★★ WU/U3 — AS DUAS FAIXAS DA CÓPIA LOCAL.
 *
 * | caso | faixa |
 * |---|---|
 * | ★★ a limpeza falhou porque **outra aba** está aberta | *"Feche as outras abas do OdontoSDR e atualize para renovar os dados"* |
 * | ★★★ a equipe chegou **sem o próprio usuário** (o sinal da Jéssica) | *"Seus dados locais estão desatualizados — Atualizar"* |
 *
 * ⚠⚠ Nenhuma das duas recarrega sozinha. ★ É a mesma razão do U2: a medição listou o trabalho
 * que um reload apaga — ★★★ incluindo **um disparo de confirmação em andamento**.
 */
export default function FaixaCopiaVelha({ recarregar }) {
  const [outraAba, setOutraAba] = useState(false);
  const [desatualizada, setDesatualizada] = useState(false);

  /**
   * ★★★ `copiaLocalPronta` é lida por import DINÂMICO e com `?.` — e isso é decisão, não enfeite.
   *
   * ⚠⚠ Esta faixa é montada pelo `AppShell`, e **todo teste de render do shell dubla
   * `@/lib/firebase`**. Um `import` estático de um export novo quebrou **17 casos de uma vez**
   * com *"No `copiaLocalPronta` export is defined on the mock"* — ★ é o mesmo padrão que já fez
   * a mutação MU-6 sobreviver na WT: **quando todos os testes dublam um módulo, acrescentar
   * export a ele é mudança de contrato com a suíte inteira.**
   *
   * ★★ Então o componente **tolera a ausência**: sem a promessa, ele simplesmente não mostra a
   * faixa de "outra aba" — que é exatamente o comportamento certo num ambiente sem Firestore.
   */
  useEffect(() => {
    let vivo = true;
    import('@/lib/firebase')
      .then((mod) => mod?.copiaLocalPronta?.then?.((r) => {
        if (vivo) setOutraAba(Boolean(r?.outraAba));
      }))
      .catch(() => {
        /**
         * ⚠ Não dá para registrar daqui: se `@/lib/firebase` não carrega, o registrador também
         * não. ★★ Mas a guarda da WT (`wt1b.catch.guardas.test.js`) está certa em exigir que a
         * falha **exista para alguém** — então ela existe no ESTADO: sem a promessa, a conclusão
         * é "não sei de outra aba", e a faixa não aparece. ★ É um `null` explícito, não um
         * silêncio.
         */
        if (vivo) setOutraAba(false);
      });
    return () => { vivo = false; };
  }, []);

  /**
   * ★★ A MARCA é lida por sondagem, de 2 em 2 s.
   *
   * ⚠ Quem a grava é o provider, quando a equipe chega incoerente — e isso acontece **segundos
   * depois** desta montagem, sem nenhum sinal que atravesse `localStorage`. ★ Sondar uma chave
   * de `localStorage` é barato; o alternativo seria um barramento de eventos só para isto.
   */
  useEffect(() => {
    const ler = () => setDesatualizada(renovacaoPedida(ultimoUid()));
    ler();
    const t = setInterval(ler, 2000);
    return () => clearInterval(t);
  }, []);

  const recarrega = recarregar ?? recarregarPagina;

  if (outraAba) {
    return (
      <div className="faixa-copia-velha" role="status">
        Feche as outras abas do OdontoSDR e atualize para renovar os dados.
      </div>
    );
  }
  if (desatualizada) {
    return (
      <div className="faixa-copia-velha" role="status">
        <span>Seus dados locais estão desatualizados.</span>
        <button type="button" onClick={recarrega}>Atualizar</button>
      </div>
    );
  }
  return null;
}
